// src/pages/MonthlyProcessingPage.jsx
// CONSOLIDATED VERSION: 3 loan types only (normal/school/emergency), simplified Adjust dialog
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { writeActivityLog } from '../utils/activityLog';
import globalApi from '../utils/globalFetchWrapper';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function fmt(n, dp = 2) {
  const v = Number(n || 0);
  return v.toLocaleString(undefined, { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function asYyyyMm(d) {
  const dt = new Date(d);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  return `${y}-${m}`;
}

function addMonths(yyyyMm, delta) {
  const [y, m] = yyyyMm.split('-').map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return asYyyyMm(d);
}

export default function MonthlyProcessingPage() {
  const navigate = useNavigate();
  const [selectedMonth, setSelectedMonth] = useState(asYyyyMm(new Date()));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [filterText, setFilterText] = useState('');

  // Simplified editing state - only 3 loan types now
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [monthSaving, setMonthSaving] = useState(false);
  const [monthSaveMsg, setMonthSaveMsg] = useState('');
  const [savedMonthView, setSavedMonthView] = useState(false);
  const [savedMonthData, setSavedMonthData] = useState(null);

  const [dragState, setDragState] = useState({
    dragging: false,
    originX: 0,
    originY: 0,
    translateX: 0,
    translateY: 0,
  });

  useEffect(() => {
    setMonthSaveMsg('');
    setSavedMonthData(null);
    setSavedMonthView(false);
    loadMonthlyData();
  }, [selectedMonth]);

  async function loadMonthlyData() {
    try {
      setErr('');
      setLoading(true);

      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/reports/monthly-processing?month=${selectedMonth}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.message || 'Failed to load monthly processing data');

      setData(j.data);

      // Auto-save silently if viewing the current month
      const currentMk = asYyyyMm(new Date());
      if (selectedMonth === currentMk) {
        autoSaveCurrentMonth(j.data);
      }
    } catch (e) {
      setErr(e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  function goToPreviousMonth() {
    setSelectedMonth(prev => addMonths(prev, -1));
  }

  function goToNextMonth() {
    setSelectedMonth(prev => addMonths(prev, 1));
  }

  async function exportExcel() {
    const token = localStorage.getItem('hopeSaccoToken');
    const url = `${API_URL}/reports/monthly-processing/export?month=${selectedMonth}`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        alert(errorData.error || 'Export failed');
        return;
      }
      
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `HopeSACCO_MonthlyDeductions_${selectedMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(downloadUrl);
    } catch (error) {
      console.error('Export error:', error);
      alert('Failed to export CSV');
    }
  }

  function viewMemberStatement(memberId) {
    navigate(`/admin/reports/member-statement?memberId=${memberId}&month=${selectedMonth}`);
  }

  // Ã¢Å“â€¦ SIMPLIFIED: Only 3 loan buckets now (normal/school/emergency)
  function openEdit(member) {
    const m = member.monthData;
    
    const buckets = [];
    if ((m.loanRepayment || 0) > 0 || (m.loanBalance || 0) > 0) {
      buckets.push({ value: 'normal', label: 'Normal Loan', currentRepayment: m.loanRepayment || 0 });
    }
    if ((m.schoolFeesRepayment || 0) > 0 || (m.schoolFeesLoan || 0) > 0) {
      buckets.push({ value: 'school', label: 'School Fees Loan', currentRepayment: m.schoolFeesRepayment || 0 });
    }
    if ((m.emergencyLoanRepayment || 0) > 0 || (m.emergencyLoan || 0) > 0) {
      buckets.push({ value: 'emergency', label: 'Emergency Loan', currentRepayment: m.emergencyLoanRepayment || 0 });
    }

    if (buckets.length === 0) {
      alert('No active loan repayments for this member in this month to adjust.');
      return;
    }

    const defaultBucket = buckets[0];

    setSaveError('');
    setEditing({
      memberId: member.memberId,
      fullName: member.fullName,
      month: selectedMonth,
      currentRepayment: defaultBucket.currentRepayment,
      newRepayment: defaultBucket.currentRepayment,
      mode: 'topup_once',
      loanBucket: defaultBucket.value,
      buckets,
    });
    
    setDragState({
      dragging: false,
      originX: 0,
      originY: 0,
      translateX: 0,
      translateY: 0,
    });
  }

  function closeEdit() {
    setEditing(null);
    setSaving(false);
    setSaveError('');
  }

  async function handleSaveAdjustment() {
    if (!editing) return;
    try {
      setSaving(true);
      setSaveError('');

      const newRepayment = Number(editing.newRepayment);
      if (Number.isNaN(newRepayment) || newRepayment < 0) {
        setSaveError('Please enter a valid new repayment amount (zero or more, no negatives).');
        setSaving(false);
        return;
      }

      const payload = {
        memberId: editing.memberId,
        month: editing.month,
        newRepayment,
        mode: editing.mode,
        loanBucket: editing.loanBucket,
      };

      const token = localStorage.getItem('hopeSaccoToken');
      const res = await fetch(`${API_URL}/reports/monthly-processing/adjust-repayment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(payload),
      });

      const json = await res.json();
      if (!res.ok || !json.success) {
        throw new Error(json.message || json.error || 'Failed to adjust repayment');
      }

      // Write audit log entry on successful save
      writeActivityLog({
        source: 'monthly_processing_adjust',
        month: editing.month,
        memberId: editing.memberId,
        memberName: editing.fullName,
        loanType: editing.loanBucket,
        mode: editing.mode,
        oldAmount: editing.currentRepayment,
        newAmount: Number(editing.newRepayment),
      });

      // Reload data to show updated values
      await loadMonthlyData();
      closeEdit();

    } catch (error) {
      setSaveError(error.message || 'Failed to adjust repayment');
    } finally {
      setSaving(false);
    }
  }

  function handleDragStart(e) {
    e.preventDefault();
    setDragState(prev => ({
      ...prev,
      dragging: true,
      originX: e.clientX - prev.translateX,
      originY: e.clientY - prev.translateY,
    }));
  }

  function handleDrag(e) {
    if (!editing) return;
    setDragState(prev => {
      if (!prev.dragging) return prev;
      const newX = e.clientX - prev.originX;
      const newY = e.clientY - prev.originY;
      return { ...prev, translateX: newX, translateY: newY };
    });
  }

  function handleDragEnd() {
    setDragState(prev => ({ ...prev, dragging: false }));
  }

  function isFutureMonth() {
    return selectedMonth > asYyyyMm(new Date());
  }

  async function saveMonthRecords() {
    if (!data || isFutureMonth()) return;
    setMonthSaving(true);
    setMonthSaveMsg('');
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const payload = {
        month: selectedMonth,
        members: data.members.map(m => ({
          memberId: m.memberId,
          fullName: m.fullName,
          monthData: m.monthData,
        })),
      };
      const r = await fetch(`${API_URL}/reports/save-monthly-records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.message || 'Failed to save');
      setMonthSaveMsg(`✅ ${j.message}`);
    } catch (e) {
      setMonthSaveMsg(`❌ ${e.message || 'Failed to save'}`);
    } finally {
      setMonthSaving(false);
    }
  }

  // Auto-save current month silently when page loads — no UI messages, runs in background
  async function autoSaveCurrentMonth(monthData) {
    if (!monthData?.members?.length) return;
    try {
      const token = localStorage.getItem('hopeSaccoToken');
      const payload = {
        month: selectedMonth,
        members: monthData.members.map(m => ({
          memberId: m.memberId,
          fullName: m.fullName,
          monthData: m.monthData,
        })),
      };
      await fetch(`${API_URL}/reports/save-monthly-records`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify(payload),
      });
      // Silent — no state update, no message shown to user
    } catch (e) {
      console.warn('Auto-save (silent):', e.message);
    }
  }

  async function toggleSavedMonthView() {
    if (savedMonthView) { setSavedMonthView(false); setSavedMonthData(null); return; }
    try {
      setSavedMonthView(true);
      const token = localStorage.getItem('hopeSaccoToken');
      const r = await fetch(`${API_URL}/reports/saved-monthly-records?month=${selectedMonth}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const j = await r.json();
      if (!j.success) throw new Error(j.message);
      setSavedMonthData(j.data);
    } catch (e) {
      setSavedMonthView(false);
      setErr(e.message || 'Failed to load saved records');
    }
  }

  let membersToShow = [];
  if (data?.members) {
    const term = (filterText || '').toLowerCase();
    membersToShow = [...data.members]
      .sort((a, b) => (a.fullName || '').localeCompare(b.fullName || ''))
      .filter(m => {
        if (!term) return true;
        const name = (m.fullName || '').toLowerCase();
        const code = (m.memberId || '').toLowerCase();
        return name.includes(term) || code.includes(term);
      });
  }

  return (
    <div style={{ padding: '20px', maxWidth: '100%', background: '#f5f5f5', minHeight: '100vh' }}>
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={{ color: '#4b3ff6', fontSize: '32px', fontWeight: 'bold', marginBottom: '10px' }}>
            Monthly Processing
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: '#666' }}>
            <span 
              onClick={() => navigate('/admin/dashboard')} 
              style={{ cursor: 'pointer', color: '#4b3ff6', textDecoration: 'none' }}
            >
              Dashboard
            </span>
            <span>›</span>
            <span style={{ color: '#333' }}>Monthly Processing</span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <button
            onClick={() => navigate('/admin/activity-log')}
            style={{ padding: '8px 16px', fontSize: '14px', background: '#4b3ff6', color: '#fff', border: 'none', borderRadius: 4, cursor: 'pointer' }}
          >
            📋 Log
          </button>
          <button onClick={() => loadMonthlyData()} disabled={loading} style={{ padding: '8px 16px', fontSize: '14px' }}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          <button onClick={exportExcel} disabled={!data} style={{ padding: '8px 16px', fontSize: '14px' }}>
            📥 Export Excel
          </button>
          <button
            onClick={saveMonthRecords}
            disabled={!data || monthSaving || isFutureMonth()}
            title={isFutureMonth() ? 'Cannot save a future month' : "Save all members' data for this month to Firebase"}
            style={{ padding: '8px 16px', fontSize: '14px', background: '#16a34a', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
          >{monthSaving ? 'Saving...' : '💾 Save Month'}</button>
          <button
            onClick={toggleSavedMonthView}
            disabled={!data}
            style={{ padding: '8px 16px', fontSize: '14px', background: savedMonthView ? '#4b3ff6' : '#6b7280', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
          >{savedMonthView ? '📋 Viewing Saved' : '📋 View Saved'}</button>
        </div>
      </div>

      <div className="container" style={{ maxWidth: '98%', background: 'white', padding: '20px', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>

        {err && <div className="alert alert-danger" style={{ marginTop: 10 }}>{err}</div>}

        <div style={{ display: 'flex', gap: 12, marginTop: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button onClick={goToPreviousMonth} style={{ padding: '8px 16px', fontSize: '14px' }}>
              ◄ Prev
            </button>
            <div>
              <label style={{ marginRight: 8 }}>Month:</label>
              <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={{ padding: '8px', fontSize: '14px' }} />
            </div>
            <button onClick={goToNextMonth} style={{ padding: '8px 16px', fontSize: '14px' }}>
              Next ►
            </button>
          </div>
        </div>

        {loading && (
          <div style={{ marginTop: 20, textAlign: 'center', color: '#666' }}>
            Loading data for all members...
          </div>
        )}

        {data && !loading && (
          <div style={{ marginTop: 20 }}>
            <div style={{ background: '#f0f0f0', padding: '10px', borderRadius: '4px', marginBottom: 10, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>Viewing: {data.month}</strong>
                <span style={{ marginLeft: 16, color: '#666' }}>
                  Total Members: {membersToShow.length} / {data.members?.length || 0}
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <input
                  type="text"
                  placeholder="Filter by name or Member ID"
                  value={filterText}
                  onChange={e => setFilterText(e.target.value)}
                  style={{ padding: '6px 10px', fontSize: 13, borderRadius: 4, border: '1px solid #ccc', minWidth: 220 }}
                />
                {filterText && (
                  <button type="button" onClick={() => setFilterText('')} style={{ padding: '4px 8px', fontSize: 12, borderRadius: 4 }}>
                    Ã¢Å“â€¢
                  </button>
                )}
              </div>

              <div style={{ color: '#4b3ff6', fontWeight: 'bold' }}>
                Grand Total: KES {fmt(data.totals?.grandTotalDeductions || 0, 2)}
              </div>
            </div>

            {monthSaveMsg && (
              <div style={{ marginTop: 8, padding: '10px 14px', borderRadius: 6, background: monthSaveMsg.startsWith('✅') ? '#dcfce7' : '#fee2e2', color: monthSaveMsg.startsWith('✅') ? '#15803d' : '#dc2626', fontWeight: 600, fontSize: 14 }}>
                {monthSaveMsg}
              </div>
            )}

            <div style={{ overflowX: 'auto' }}>
              <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                <table className="table table-striped" style={{ fontSize: '13px', width: '100%' }}>
                  <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                    <tr>
                      <th style={{ minWidth: '100px' }}>Member ID</th>
                      <th style={{ minWidth: '150px' }}>Name</th>
                      <th className="text-right">Shares</th>
                      <th className="text-right">Shares Contri.</th>

                      {/* NORMAL LOAN - Consolidated */}
                      <th className="text-right">Loan</th>
                      <th className="text-right">Loan Int.</th>
                      <th className="text-right">Repayment</th>

                      {/* SCHOOL LOAN - Consolidated */}
                      <th className="text-right">School</th>
                      <th className="text-right">School Int.</th>
                      <th className="text-right">School Rep.</th>

                      {/* EMERGENCY LOAN - Consolidated */}
                      <th className="text-right">Emerg.</th>
                      <th className="text-right">Emerg. Int.</th>
                      <th className="text-right">Emerg. Rep.</th>

                      <th className="text-right">Appl. Fees</th>
                      <th className="text-right"><strong>Total Deductions</strong></th>
                      <th style={{ minWidth: '120px' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {membersToShow.map((member, i) => {
                      const m = member.monthData;
                      const hasActiveLoans = m.loanBalance > 0 || m.schoolFeesLoan > 0 || m.emergencyLoan > 0;

                      return (
                        <tr
                          key={i}
                          style={{ cursor: 'pointer', background: hasActiveLoans ? '#fff9e6' : 'transparent' }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#bfdbfe')}
                          onMouseLeave={e => (e.currentTarget.style.background = hasActiveLoans ? '#fff9e6' : 'transparent')}
                        >
                          <td onClick={() => viewMemberStatement(member.memberId)}><strong>{member.memberId}</strong></td>
                          <td onClick={() => viewMemberStatement(member.memberId)}>{member.fullName}</td>
                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.shares, 0)}</td>
                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.sharesContribution, 2)}</td>

                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.loanBalance, 2)}</td>
                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.loanInterest, 2)}</td>
                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.loanRepayment, 0)}</td>

                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.schoolFeesLoan, 2)}</td>
                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.schoolFeesInterest, 2)}</td>
                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.schoolFeesRepayment, 0)}</td>

                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.emergencyLoan, 2)}</td>
                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.emergencyLoanInterest, 2)}</td>
                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.emergencyLoanRepayment, 0)}</td>

                          <td className="text-right" onClick={() => viewMemberStatement(member.memberId)}>{fmt(m.applicationFees, 2)}</td>
                          <td className="text-right" style={{ fontWeight: 'bold' }} onClick={() => viewMemberStatement(member.memberId)}>
                            {fmt(m.totalDeductions, 2)}
                          </td>

                          <td>
                            <button
                              type="button"
                              className="btn btn-xs btn-outline"
                              onClick={e => {
                                e.stopPropagation();
                                openEdit(member);
                              }}
                            >
                              Adjust
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>

                  <tfoot>
                    <tr style={{ background: '#e8f4f8', fontWeight: 'bold', fontSize: '14px' }}>
                      <td colSpan="2"><strong>TOTALS</strong></td>
                      <td className="text-right">{fmt(data.totals.totalShares, 2)}</td>
                      <td className="text-right">{fmt(data.totals.totalSharesContributed, 2)}</td>

                      <td className="text-right">{fmt(data.totals.totalLoanBalance, 2)}</td>
                      <td className="text-right">{fmt(data.totals.totalLoanInterest, 2)}</td>
                      <td className="text-right">{fmt(data.totals.totalLoanRepayments, 2)}</td>

                      <td className="text-right">{fmt(data.totals.totalSchoolLoan, 2)}</td>
                      <td className="text-right">{fmt(data.totals.totalSchoolInterest, 2)}</td>
                      <td className="text-right">{fmt(data.totals.totalSchoolRepayment, 2)}</td>

                      <td className="text-right">{fmt(data.totals.totalEmergencyLoan, 2)}</td>
                      <td className="text-right">{fmt(data.totals.totalEmergencyInterest, 2)}</td>
                      <td className="text-right">{fmt(data.totals.totalEmergencyRepayment, 2)}</td>

                      <td className="text-right">{fmt(data.totals.totalApplicationFees, 2)}</td>
                      <td className="text-right" style={{ fontSize: '15px' }}>{fmt(data.totals.grandTotalDeductions, 2)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Saved Monthly Records View */}
      {savedMonthView && savedMonthData && (
        <div style={{ marginTop: 24, background: 'white', padding: 20, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          <h3 style={{ color: '#4b3ff6', marginBottom: 10, fontSize: 18 }}>
            📋 Saved Records — {savedMonthData.monthDisplay}&nbsp;
            <span style={{ fontSize: 14, fontWeight: 400, color: '#555' }}>
              {savedMonthData.savedCount} member(s) saved  
              {savedMonthData.processedCount > 0 && (
                <span style={{ background: '#fef9c3', color: '#92400e', fontSize: 12, padding: '2px 8px', borderRadius: 10, marginRight: 6 }}>
                  ✅ {savedMonthData.processedCount} payroll-processed
                </span>
              )}
              {savedMonthData.committedCount > 0 && (
                <span style={{ background: '#dcfce7', color: '#15803d', fontSize: 12, padding: '2px 8px', borderRadius: 10 }}>
                  💾 {savedMonthData.committedCount} committed
                </span>
              )}
            </span>
          </h3>
          <div style={{ marginBottom: 10, display: 'flex', justifyContent: 'flex-end' }}>
            <button
              onClick={() => {
                if (!savedMonthData) return;
                let csv = `Member ID,Name,Status,Saved By,Shares,Shares Contri.,Loan,Loan Int.,Repayment,School,School Int.,School Rep.,Emerg.,Emerg. Int.,Emerg. Rep.,Appl. Fees,Total Deductions\n`;
                savedMonthData.members.forEach(m => {
                  const d = m.monthData;
                  csv += [m.memberId, `"${m.fullName}"`, m.status, m.savedBy,
                    d.shares, d.sharesContribution, d.loanBalance, d.loanInterest, d.loanRepayment,
                    d.schoolFeesLoan, d.schoolFeesInterest, d.schoolFeesRepayment,
                    d.emergencyLoan, d.emergencyLoanInterest, d.emergencyLoanRepayment,
                    d.applicationFees, d.totalDeductions].join(',') + '\n';
                });
                const t = savedMonthData.totals;
                csv += `TOTALS,,,,${t.shares},${t.sharesContribution},${t.loanBalance},${t.loanInterest},${t.loanRepayment},${t.schoolFeesLoan},${t.schoolFeesInterest},${t.schoolFeesRepayment},${t.emergencyLoan},${t.emergencyLoanInterest},${t.emergencyLoanRepayment},${t.applicationFees},${t.totalDeductions}\n`;
                const blob = new Blob([csv], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url; a.download = `saved-monthly-${savedMonthData.month || selectedMonth}.csv`;
                document.body.appendChild(a); a.click();
                document.body.removeChild(a); window.URL.revokeObjectURL(url);
              }}
              style={{ padding: '6px 14px', fontSize: 13, background: '#0891b2', color: 'white', border: 'none', borderRadius: 4, cursor: 'pointer', fontWeight: 600 }}
            >📥 Export Saved CSV</button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <div style={{ maxHeight: '55vh', overflowY: 'auto' }}>
              <table className="table table-striped" style={{ fontSize: '13px', width: '100%' }}>
                <thead style={{ position: 'sticky', top: 0, background: '#fff', zIndex: 2 }}>
                  <tr>
                    <th>Member ID</th><th>Name</th><th>Status</th><th>Saved By</th>
                    <th className="text-right">Shares</th>
                    <th className="text-right">Shares Contri.</th>
                    <th className="text-right">Loan</th>
                    <th className="text-right">Loan Int.</th>
                    <th className="text-right">Repayment</th>
                    <th className="text-right">School</th>
                    <th className="text-right">School Int.</th>
                    <th className="text-right">School Rep.</th>
                    <th className="text-right">Emerg.</th>
                    <th className="text-right">Emerg. Int.</th>
                    <th className="text-right">Emerg. Rep.</th>
                    <th className="text-right">Appl. Fees</th>
                    <th className="text-right"><strong>Total Deductions</strong></th>
                  </tr>
                </thead>
                <tbody>
                  {savedMonthData.members.map((member, i) => {
                    const m = member.monthData;
                    return (
                      <tr key={i} style={{ background: member.status === 'processed' ? '#fefce8' : '#f0fdf4' }}>
                        <td><strong>{member.memberId}</strong></td>
                        <td>{member.fullName}</td>
                        <td style={{ textAlign: 'center', fontSize: 12 }}>
                          {member.status === 'processed'
                            ? <span style={{ background: '#fef9c3', color: '#92400e', padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>✅ Payroll</span>
                            : <span style={{ background: '#dcfce7', color: '#15803d', padding: '1px 6px', borderRadius: 8, fontWeight: 600 }}>💾 Saved</span>
                          }
                        </td>

                        <td style={{ fontSize: 11, color: '#6b7280' }} title={member.savedAt ? new Date(member.savedAt).toLocaleString() : ''}>
                          {member.savedBy || '—'}
                        </td>
                        <td className="text-right">{fmt(m.shares, 0)}</td>
                        <td className="text-right">{fmt(m.sharesContribution, 2)}</td>
                        <td className="text-right">{fmt(m.loanBalance, 2)}</td>
                        <td className="text-right">{fmt(m.loanInterest, 2)}</td>
                        <td className="text-right">{fmt(m.loanRepayment, 0)}</td>
                        <td className="text-right">{fmt(m.schoolFeesLoan, 2)}</td>
                        <td className="text-right">{fmt(m.schoolFeesInterest, 2)}</td>
                        <td className="text-right">{fmt(m.schoolFeesRepayment, 0)}</td>
                        <td className="text-right">{fmt(m.emergencyLoan, 2)}</td>
                        <td className="text-right">{fmt(m.emergencyLoanInterest, 2)}</td>
                        <td className="text-right">{fmt(m.emergencyLoanRepayment, 0)}</td>
                        <td className="text-right">{fmt(m.applicationFees, 2)}</td>
                        <td className="text-right" style={{ fontWeight: 'bold' }}>{fmt(m.totalDeductions, 2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#e8f4f8', fontWeight: 'bold' }}>
                    <td colSpan={4}><strong>TOTALS</strong></td>
                    <td className="text-right">{fmt(savedMonthData.totals.shares, 0)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.sharesContribution, 2)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.loanBalance, 2)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.loanInterest, 2)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.loanRepayment, 0)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.schoolFeesLoan, 2)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.schoolFeesInterest, 2)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.schoolFeesRepayment, 0)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.emergencyLoan, 2)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.emergencyLoanInterest, 2)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.emergencyLoanRepayment, 0)}</td>
                    <td className="text-right">{fmt(savedMonthData.totals.applicationFees, 2)}</td>
                    <td className="text-right" style={{ fontSize: 14 }}>{fmt(savedMonthData.totals.totalDeductions, 2)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {savedMonthView && !savedMonthData && (
        <div style={{ marginTop: 20, padding: 16, background: '#f3f4f6', borderRadius: 8, color: '#6b7280' }}>
          No saved records found for {selectedMonth}. Click Save Month first.
        </div>
      )}

      {/* Ã¢Å“â€¦ SIMPLIFIED Adjust Modal - Only 3 loan types */}
      {editing && (
        <div
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}
          onMouseMove={handleDrag}
          onMouseUp={handleDragEnd}
          onMouseLeave={handleDragEnd}
        >
          <div
            style={{
              background: 'white',
              padding: 20,
              borderRadius: 8,
              width: '100%',
              maxWidth: 480,
              boxShadow: '0 10px 25px rgba(0,0,0,0.2)',
              transform: `translate(${dragState.translateX}px, ${dragState.translateY}px)`,
              cursor: dragState.dragging ? 'grabbing' : 'default',
            }}
          >
            <div style={{ cursor: 'grab', paddingBottom: 4 }} onMouseDown={handleDragStart}>
              <h3 style={{ marginTop: 0, marginBottom: 4, color: '#4b3ff6' }}>
                Adjust Repayment Ã¢â‚¬â€œ {editing.fullName}
              </h3>
              <p style={{ marginTop: 0, fontSize: 13, color: '#555' }}>
                Month: <strong>{editing.month}</strong>
              </p>
            </div>

            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 4 }}>
                Loan Type
              </label>
              <select
                value={editing.loanBucket}
                onChange={e => {
                  const bucket = editing.buckets.find(b => b.value === e.target.value);
                  setEditing(prev => ({
                    ...prev,
                    loanBucket: e.target.value,
                    currentRepayment: bucket.currentRepayment,
                    newRepayment: bucket.currentRepayment,
                  }));
                }}
                style={{ width: '100%', padding: 8, fontSize: 14 }}
              >
                {editing.buckets.map(b => (
                  <option key={b.value} value={b.value}>
                    {b.label} (Current: {fmt(b.currentRepayment, 0)})
                  </option>
                ))}
              </select>
            </div>

            <div style={{ marginTop: 10 }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 4 }}>Adjustment Type</label>
              <label style={{ display: 'block', fontSize: 13 }}>
                <input type="radio" name="mode" value="topup_once" checked={editing.mode === 'topup_once'} onChange={() => setEditing(prev => ({ ...prev, mode: 'topup_once' }))} style={{ marginRight: 6 }} />
                Top-up once (only this month)
              </label>
              <label style={{ display: 'block', fontSize: 13, marginTop: 4 }}>
                <input type="radio" name="mode" value="schedule_change" checked={editing.mode === 'schedule_change'} onChange={() => setEditing(prev => ({ ...prev, mode: 'schedule_change' }))} style={{ marginRight: 6 }} />
                Schedule change (this and all future months)
              </label>
            </div>

            <div style={{ marginTop: 12 }}>
              <label style={{ display: 'block', fontWeight: 'bold', marginBottom: 4 }}>
                New Repayment Amount
              </label>
              <input
                type="number"
                min="0"
                step="1"
                value={editing.newRepayment}
                onChange={e => setEditing(prev => ({ ...prev, newRepayment: e.target.value }))}
                style={{ width: '100%', padding: 8, fontSize: 14 }}
              />
            </div>

            {saveError && (
              <div className="alert alert-danger" style={{ marginTop: 8, padding: 8, fontSize: 12 }}>
                {saveError}
              </div>
            )}

            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button type="button" onClick={closeEdit} disabled={saving}>Cancel</button>
              <button type="button" className="btn btn-primary" onClick={handleSaveAdjustment} disabled={saving}>
                {saving ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}